import type { CardId, WallCard, WallId } from '@walls/store/types';
import type { WallsClient } from './client';

export class CardsApi {
  constructor(private readonly client: WallsClient) {}

  getCardImageUrl = (boardId: WallId, cardId: CardId) => {
    return this.client.getAssetUrl(`/boards/cards/image/${boardId}/${cardId}`);
  };

  getCards = (boardId: WallId, key?: string | null) => {
    return this.client.requestJson<WallCard[]>(`/boards/cards/${boardId}`, boardId, key);
  };

  insertTextCard = (
    boardId: WallId,
    shape: string,
    content: string,
    color: string,
    key?: string | null
  ) => {
    return this.client.requestJson<WallCard>(`/boards/cards/text/${boardId}`, boardId, key, {
      method: 'POST',
      body: JSON.stringify({
        assignedTo: null,
        color,
        content,
        shape,
        timeSpan: null,
        timeSpanUnit: null,
      }),
    });
  };

  updateTextCard = (
    boardId: WallId,
    cardId: CardId,
    content: string,
    color: string,
    key?: string | null
  ) => {
    return this.client.requestJson<WallCard>(
      `/boards/cards/text/${boardId}/${cardId}`,
      boardId,
      key,
      {
        method: 'PUT',
        body: JSON.stringify({
          assignedTo: null,
          color,
          content,
          timeSpan: null,
          timeSpanUnit: null,
        }),
      }
    );
  };

  updateImageCard = (
    boardId: WallId,
    cardId: CardId,
    title: string,
    color: string,
    key?: string | null
  ) => {
    return this.client.requestJson<WallCard>(
      `/boards/cards/image/${boardId}/${cardId}`,
      boardId,
      key,
      {
        method: 'PUT',
        body: JSON.stringify({
          assignedTo: null,
          boardId,
          color,
          id: cardId,
          timeSpan: null,
          timeSpanUnit: null,
          title,
        }),
      }
    );
  };

  updateCardPosition = (
    boardId: WallId,
    cardId: CardId,
    position: { id: CardId; xPos: number; yPos: number },
    key?: string | null
  ) => {
    return this.client.requestJson<WallCard>(
      `/boards/cards/position/${boardId}/${cardId}`,
      boardId,
      key,
      {
        method: 'PUT',
        body: JSON.stringify(position),
      }
    );
  };

  updateCardSize = (
    boardId: WallId,
    cardId: CardId,
    size: { height: number | null; id: CardId; width: number | null },
    key?: string | null
  ) => {
    return this.client.requestJson<WallCard>(
      `/boards/cards/resize/${boardId}/${cardId}`,
      boardId,
      key,
      {
        method: 'PUT',
        body: JSON.stringify(size),
      }
    );
  };

  duplicateCard = (boardId: WallId, cardId: CardId, key?: string | null) => {
    return this.client.requestJson<WallCard>(
      `/boards/cards/duplicate/${boardId}/${cardId}`,
      boardId,
      key,
      {
        method: 'POST',
      }
    );
  };

  lockCard = (boardId: WallId, cardId: CardId, key?: string | null) => {
    return this.client.requestJson<void>(`/boards/cards/lock/${boardId}/${cardId}`, boardId, key, {
      method: 'PUT',
    });
  };

  unlockCard = (boardId: WallId, cardId: CardId, key?: string | null) => {
    return this.client.requestJson<void>(
      `/boards/cards/unlock/${boardId}/${cardId}`,
      boardId,
      key,
      {
        method: 'PUT',
      }
    );
  };

  convertCardToCluster = (boardId: WallId, cardId: CardId, key?: string | null) => {
    return this.client.requestJson<WallCard>(
      `/boards/cards/cluster/${boardId}/${cardId}`,
      boardId,
      key,
      {
        method: 'PUT',
        body: JSON.stringify({ persistCluster: true }),
      }
    );
  };

  unclusterCard = (boardId: WallId, cardId: CardId, key?: string | null) => {
    return this.client.requestJson<void>(
      `/boards/cards/cluster/${boardId}/${cardId}`,
      boardId,
      key,
      {
        method: 'PUT',
        body: JSON.stringify({ persistCluster: false }),
      }
    );
  };

  updateCardZIndexes = (
    boardId: WallId,
    cards: Array<{ cardId: CardId; zPos: number }>,
    key?: string | null
  ) => {
    return this.client.requestJson<void>(`/boards/cards/zindex/${boardId}`, boardId, key, {
      method: 'PUT',
      body: JSON.stringify({ cards }),
    });
  };

  setCardColor = (boardId: WallId, cardIds: CardId[], color: string, key?: string | null) => {
    return this.client.requestJson<void>(`/boards/cards/color/${boardId}`, boardId, key, {
      method: 'PUT',
      body: JSON.stringify({ cardIds, color }),
    });
  };

  deleteCard = (boardId: WallId, cardId: CardId, key?: string | null) => {
    return this.client.requestJson<void>(`/boards/cards/${boardId}/${cardId}`, boardId, key, {
      method: 'DELETE',
    });
  };

  uploadImageCards = (
    boardId: WallId,
    files: File[],
    title: string,
    color: string,
    key?: string | null
  ) => {
    const formData = new FormData();

    files.forEach((file) => formData.append('file', file));
    formData.append('title', title);
    formData.append('color', color);

    return this.client.requestFormJson<WallCard[]>(
      `/boards/cards/image/${boardId}`,
      boardId,
      key,
      formData
    );
  };
}
